import { useState } from "react";
import { opApi, apiError } from "@/lib/api";
import { Popover, PopoverTrigger, PopoverContent } from "@/components/ui/popover";
import { Button } from "@/components/ui/button";
import { Progress } from "@/components/ui/progress";
import { toast } from "sonner";
import { CreditCard, Gift } from "lucide-react";

const COLOR = {
  green: "text-[hsl(var(--success))]",
  red: "text-red-500",
  blue: "text-primary",
  grey: "text-muted-foreground/40",
};
const LABEL = {
  green: "Rewards available",
  red: "Points expiring within 30 days",
  blue: "Enrolled in loyalty",
  grey: "Not enrolled in loyalty",
};

export function CustomerLoyaltyCell({ customerId, color = "grey", onChange, readOnly = false }) {
  const [detail, setDetail] = useState(null);
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const [d, o] = await Promise.all([
        opApi.get(`/tenant/customers/${customerId}/loyalty`),
        opApi.get(`/tenant/customers/${customerId}/orders`),
      ]);
      setDetail(d.data); setOrders(o.data);
    }
    catch (e) { toast.error(apiError(e.response?.data?.detail)); }
    finally { setLoading(false); }
  };
  const redeem = async (m) => {
    try { await opApi.post(`/tenant/loyalty/memberships/${m.id}/redeem`); toast.success("Reward redeemed"); load(); onChange && onChange(); }
    catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };
  const adjust = async (m, delta) => {
    const next = Math.max(0, (m.progress || 0) + delta);
    try { await opApi.put(`/tenant/loyalty/memberships/${m.id}`, { progress: next }); load(); onChange && onChange(); }
    catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };

  return (
    <Popover onOpenChange={(o) => { if (o) load(); }}>
      <PopoverTrigger asChild>
        <button className={`inline-flex items-center ${COLOR[color] || COLOR.grey}`} title={LABEL[color]} data-testid={`loyalty-icon-${customerId}`}>
          <CreditCard className="h-4 w-4" />
        </button>
      </PopoverTrigger>
      <PopoverContent className="w-80 rounded-none" align="start" data-testid={`loyalty-popover-${customerId}`}>
        <div className="text-xs uppercase tracking-wider text-muted-foreground mb-2">Loyalty cards</div>
        {loading && <div className="text-sm text-muted-foreground">Loading…</div>}
        {!loading && detail && detail.length === 0 && <div className="text-sm text-muted-foreground">Not enrolled in any program.</div>}
        {!loading && detail && detail.map((m) => (
          <div key={m.id} className="border-t border-border py-2.5" data-testid={`loyalty-membership-${m.id}`}>
            <div className="flex items-center justify-between gap-2">
              <span className="font-medium text-sm">{m.program_title}</span>
              <span className={`text-[10px] uppercase font-mono px-1.5 py-0.5 ${m.color === "red" ? "bg-red-500 text-white" : "bg-[hsl(var(--success))] text-white"}`}>{m.color === "red" ? "expiring" : "active"}</span>
            </div>
            <div className="text-xs text-muted-foreground">{m.reward}</div>
            <div className="mt-1.5 flex items-center gap-2">
              <div className="flex-1"><Progress value={Math.min(100, (m.progress / Math.max(1, m.goal)) * 100)} className="h-1.5" /></div>
              <span className="font-mono text-xs">{m.progress}/{m.goal}</span>
            </div>
            {(m.ready_rewards || []).length > 0 && (
              <div className="mt-1.5 flex flex-wrap gap-1" data-testid={`loyalty-ready-${m.id}`}>
                {m.ready_rewards.map((rr, i) => (
                  <span key={i} className="text-[11px] font-semibold px-1.5 py-0.5 bg-[hsl(var(--success))] text-white inline-flex items-center gap-1"><Gift className="h-3 w-3" />{rr.reward}</span>
                ))}
              </div>
            )}
            {!readOnly ? (
              <div className="mt-2 flex items-center gap-1.5">
                <Button size="sm" variant="outline" className="rounded-none h-6 w-6 p-0 text-xs" onClick={() => adjust(m, -1)} data-testid={`loyalty-minus-${m.id}`}>−</Button>
                <Button size="sm" variant="outline" className="rounded-none h-6 w-6 p-0 text-xs" onClick={() => adjust(m, 1)} data-testid={`loyalty-plus-${m.id}`}>+</Button>
                {m.rewards_ready > 0 && (
                  <Button size="sm" className="rounded-none h-6 ml-auto text-xs gap-1" onClick={() => redeem(m)} data-testid={`loyalty-redeem-${m.id}`}><Gift className="h-3 w-3" /> Redeem ({m.rewards_ready})</Button>
                )}
              </div>
            ) : m.rewards_ready > 0 ? (
              <div className="mt-1.5 text-[11px] text-muted-foreground">Select this reward below the customer to apply it to the ticket.</div>
            ) : null}
          </div>
        ))}
        {!loading && (
          <div className="border-t border-border pt-2 mt-2" data-testid={`customer-orders-${customerId}`}>
            <div className="text-xs uppercase tracking-wider text-muted-foreground mb-1.5">Recent orders</div>
            {orders.length ? orders.slice(0, 5).map((o) => (
              <div key={o.id} className="flex items-center justify-between text-xs py-1">
                <span className="font-mono text-muted-foreground">#{o.order_number}</span>
                <span className="capitalize">{o.status}</span>
                <span className="font-mono">${Number(o.total).toFixed(2)}</span>
              </div>
            )) : <div className="text-xs text-muted-foreground">No orders yet.</div>}
          </div>
        )}
      </PopoverContent>
    </Popover>
  );
}
